import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { initializeApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Composants stylisés
const RedirectContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
  padding: 2rem;
  text-align: center;
`;

const Logo = styled.img`
  width: 120px;
  height: auto;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.primary};
`;

const Description = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  max-width: 600px;
  line-height: 1.6;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StoreButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.8rem 1.5rem;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border-radius: 50px;
  text-decoration: none;
  font-weight: bold;
  transition: all 0.3s ease;
  gap: 0.5rem;
  min-width: 200px;

  &:hover {
    background-color: ${props => props.theme.colors.secondary};
    transform: translateY(-2px);
  }

  img {
    width: 24px;
    height: 24px;
  }
`;

const DirectButton = styled(StoreButton)`
  background-color: ${props => props.theme.colors.secondary};
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
  }
`;

const Message = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 8px;
  background-color: ${props => props.success ? '#e6f7e6' : '#fff3f3'};
  color: ${props => props.success ? '#2e7d32' : '#d32f2f'};
  font-weight: 500;
`;

const CountdownText = styled.div`
  margin-top: 1rem;
  font-size: 1rem;
  color: ${props => props.theme.colors.textSecondary};
`;

// Configuration des liens de téléchargement
const APP_LINKS = {
  ios: "https://apps.apple.com/app/xenya/id1234567890",
  android: "https://play.google.com/store/apps/details?id=com.xenya.app",
  appScheme: "xenya://"
};

// Configuration Firebase
const firebaseConfig = {
  apiKey: "AIzaSyDl3GaCQNZEwlpYp4d3sBDYzgsmRdskxbE",
  authDomain: "xenya-app.firebaseapp.com",
  projectId: "xenya-app",
  storageBucket: "xenya-app.firebasestorage.app",
  messagingSenderId: "1098647608082",
  appId: "1:1098647608082:web:6db224888ed63c59c6f1cb",
  measurementId: "G-R0YFWMW4G2"
};

// Initialiser Firebase
const app = initializeApp(firebaseConfig);
const functions = getFunctions(app);

// Utilitaires pour la gestion des cookies
const setCookie = (name, value, days) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = "; expires=" + date.toUTCString();
  document.cookie = name + "=" + (value || "") + expires + "; path=/; SameSite=Lax";
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Composant pour gérer les redirections de deeplinks
 * Prend en charge les formats:
 * - /ref/:code (code d'affiliation)
 * - /download (téléchargement de l'application)
 * - Autres deep links pour l'application native
 */
// Fonction pour collecter l'empreinte digitale de l'appareil
const collectDeviceFingerprint = (currentPlatform) => {
  // Données de base pour l'empreinte digitale
  const fingerprint = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    platform: currentPlatform,  // Déjà détecté
    colorDepth: window.screen.colorDepth,
    deviceMemory: navigator.deviceMemory || 'unknown',
    hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
    sessionId: generateSessionId(),
    timestamp: new Date().toISOString(),
    referrer: document.referrer || 'direct'
  };
  
  return fingerprint;
};

// Générer un ID de session unique et le stocker dans un cookie
const generateSessionId = () => {
  let sessionId = getCookie('xenya_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 10);
    setCookie('xenya_session_id', sessionId, 30); // 30 jours d'expiration
  }
  return sessionId;
};

// Stocker les données de pré-installation avec l'empreinte digitale
const storePreInstallData = async (fingerprintData, deeplinkData, pathname, search) => {
  try {
    // Appeler la fonction Firebase
    const storeData = httpsCallable(functions, 'storePreInstallData');
    const result = await storeData({
      fingerprint: fingerprintData,
      deeplink: deeplinkData,
      path: pathname,
      query: search
    });
    
    console.log('Données de pré-installation stockées:', result.data);
    
    // Stocker l'ID de suivi dans un cookie pour une utilisation future potentielle
    if (result.data && result.data.trackingId) {
      setCookie('xenya_tracking_id', result.data.trackingId, 90); // 90 jours d'expiration
    }
    
    return result.data;
  } catch (error) {
    console.error('Erreur lors du stockage des données de pré-installation:', error);
    return null;
  }
};

const DeeplinkRedirect = () => {
  const { t } = useTranslation();
  const { type, code } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [message, setMessage] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [redirectAttempted, setRedirectAttempted] = useState(false);
  const [fingerprinting, setFingerprinting] = useState(false);

  // Détecter la plateforme de l'utilisateur
  const [platform, setPlatform] = useState('unknown');
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent)) {
      setPlatform('android');
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setPlatform('ios');
    } else {
      setPlatform('desktop');
    }
  }, []);

  // Construire l'URL de redirection
  const buildRedirectUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    
    // Récupérer le chemin complet actuel de l'URL
    let path = '';
    
    // Si nous sommes sur une route spécifique avec un code (ex: /ref/ABC123)
    if (location.pathname) {
      // Utiliser le chemin actuel (en supprimant le premier slash)
      path = location.pathname.startsWith('/') 
        ? location.pathname.substring(1) 
        : location.pathname;
    } 
    // Fallback: construire le chemin à partir des paramètres
    else if (type === 'ref' && code) {
      path = `ref/${code}`;
    } else if (type) {
      path = type;
      // Ajouter les paramètres spécifiques si nécessaires
      if (code) {
        path += `/${code}`;
      }
    }

    // Construire l'URL complète avec tous les paramètres
    let appUrl = `${APP_LINKS.appScheme}${path}`;
    
    // Ajouter les paramètres d'URL si présents
    const queryParams = [];
    searchParams.forEach((value, key) => {
      queryParams.push(`${key}=${value}`);
    });
    
    if (queryParams.length > 0) {
      appUrl += `?${queryParams.join('&')}`;
    }
    
    // Log pour déboguer
    console.log('URL de redirection: ', appUrl);
    
    return appUrl;
  };

  // Gérer la redirection mobile
  const redirectToApp = async () => {
    // Vérifier si nous avons déjà tenté une empreinte digitale
    if (fingerprinting) return;
    
    const appUrl = buildRedirectUrl();
    setRedirectAttempted(true);
    setFingerprinting(true);
    
    try {
      // Collecter l'empreinte digitale avant la redirection
      const fingerprint = collectDeviceFingerprint(platform);
      
      // Stocker les données de pré-installation
      await storePreInstallData(fingerprint, {
        type,
        code,
        fullPath: location.pathname + location.search
      }, location.pathname, location.search);
      
      console.log("Empreinte digitale collectée et stockée");
    } catch (error) {
      console.error("Erreur lors de la collecte d'empreinte digitale:", error);
      // Continuer malgré l'erreur pour ne pas bloquer la redirection
    } finally {
      setFingerprinting(false);
    }
    
    // Sur iOS, on peut utiliser une approche simple car Safari gère bien les schémas d'URL
    if (platform === 'ios') {
      window.location.href = appUrl;
      
      // Timeout pour rediriger vers l'App Store si l'ouverture de l'app échoue
      setTimeout(() => {
        window.location.href = APP_LINKS.ios;
      }, 2500);
    } 
    // Sur Android, utiliser une approche différente car Chrome gère les schémas d'URL différemment
    else if (platform === 'android') {
      // Essayer d'ouvrir l'app
      window.location.href = appUrl;
      
      // Timeout pour rediriger vers le Play Store si l'ouverture de l'app échoue
      setTimeout(() => {
        window.location.href = APP_LINKS.android;
      }, 2500);
    } 
    // Pour les utilisateurs desktop, fournir les liens de téléchargement
    else {
      setMessage({
        text: t('deeplink.desktop_message'),
        success: true
      });
    }
  };

  // Redirection automatique immédiate pour iOS
  useEffect(() => {
    if (platform === 'ios' && !redirectAttempted) {
      // Redirection immédiate sur iOS
      setTimeout(() => {
        redirectToApp();
      }, 300);
    }
  }, [platform, redirectAttempted]);

  // Effectuer la redirection automatique pour les autres plateformes
  useEffect(() => {
    // Ne pas démarrer le timer sur iOS (car on redirige immédiatement)
    if (platform === 'ios') return;
    
    // Décompte avant redirection automatique
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Rediriger automatiquement à la fin du compte à rebours
          if (!redirectAttempted) {
            redirectToApp();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [platform, redirectAttempted]);

  // Déterminer le texte de redirection selon le type
  const getRedirectMessage = () => {
    if (type === 'ref' && code) {
      return t('deeplink.referral_message', { code });
    } else if (type === 'download') {
      return t('deeplink.download_message');
    } else {
      return t('deeplink.general_message');
    }
  };

  return (
    <RedirectContainer>
      <Logo src="/assets/icon.png" alt="Xenya" />
      <Title>{t('deeplink.title')}</Title>
      <Description>{getRedirectMessage()}</Description>
      
      <ButtonContainer>
        <StoreButton href={APP_LINKS.ios} target="_blank" rel="noopener noreferrer">
          <img src="/assets/app-store-logo.png" alt="App Store" />
          {t('deeplink.ios_button')}
        </StoreButton>
        
        <StoreButton href={APP_LINKS.android} target="_blank" rel="noopener noreferrer">
          <img src="/assets/play-store-logo.png" alt="Play Store" />
          {t('deeplink.android_button')}
        </StoreButton>
        
        {!redirectAttempted && (
          <DirectButton onClick={redirectToApp}>
            {t('deeplink.open_app_button')}
          </DirectButton>
        )}
      </ButtonContainer>
      
      {message && (
        <Message success={message.success}>
          {message.text}
        </Message>
      )}
      
      {!redirectAttempted && platform !== 'ios' && (
        <CountdownText>
          {t('deeplink.auto_redirect', { seconds: countdown })}
        </CountdownText>
      )}
      
      {platform === 'ios' && !redirectAttempted && (
        <CountdownText>
          {t('deeplink.app_store_redirect')}
        </CountdownText>
      )}
    </RedirectContainer>
  );
};

export default DeeplinkRedirect;